// The ONE table for the words and steps of a ticket, keyed by kind.
//
// A support ticket is a library's request; an engineering ticket is a piece of
// work for the engineering team (see docs/stage-reports/2026-tickets-kind-screens.md).
// Every picker, filter, bulk action, board lane, badge and summary tile on the
// ticket screens reads from here. Nothing else on those screens lists statuses
// or types, so when engineering decides which steps and types they want, THIS is
// the only frontend file that changes. (The server keeps its own matching table.)
//
// The support entries reproduce what the screens showed before engineering
// tickets existed: same types, same statuses, same labels, same lanes, same
// order. ticketVocabulary.test.ts pins them to those literal values.
import { displayLabel, TicketStatus, ENGINEERING_ONLY_TICKET_STATUS_LABELS } from './displayLabels.js';

export type TicketKind = 'support' | 'engineering';

export type StatusTone = 'green' | 'blue' | 'amber' | 'red' | 'grey' | 'teal';

export interface VocabOption {
	value: string;
	label: string;
}

export interface VocabStatus extends VocabOption {
	tone: StatusTone;
	// A finished ticket stops counting as open work. The board never shows the
	// ones with no lane, and the list stops showing a due date.
	finished: boolean;
	// The requester's words for where their change is when an agent crew is
	// driving the engineering ticket (20 October 2026): the support side's chip.
	// Engineering only; a step with none reads as its label.
	agentWords?: string;
}

export interface VocabLane {
	key: string;
	label: string;
	// Every stored status the lane shows. A status may appear in one lane only.
	statuses: string[];
	// The status a card dropped on the lane takes.
	dropStatus: string;
	// The lane shows its cards in backlog order (placed ones by rank, then the rest). It belongs
	// to every lane of the product board and to Engineer Pickup on the engineering board
	// (contract section 11 h with section 21 c); nothing after the hand over is a backlog.
	backlogOrder?: boolean;
}

// The two boards of an engineering ticket (contract section 21 c). `board` is the
// engineering board (the Kanban view), `product` is the product board. Support
// has the one board.
export type BoardKey = 'board' | 'product';

export interface VocabTile {
	key: string;
	label: string;
	tone: 'neutral' | 'danger' | 'warning' | 'grey';
	// What a click applies to the list. Support tiles use the existing `status`
	// and `risk` intents; engineering tiles are plain list filters so the list
	// and the count agree (and so the tile can be a real link).
	filter: Record<string, string>;
}

export interface KindVocabulary {
	kind: TicketKind;
	label: string;
	types: VocabOption[];
	// The statuses offered, in order, for every picker, filter and bulk action.
	statuses: VocabStatus[];
	// The statuses a ticket's own page offers in its Status dropdown. Support's is
	// every status the product knows (today's behaviour on that page, including
	// grandfathered ones); engineering's is the same list as `statuses`.
	pageStatuses: VocabOption[];
	// The one board of support, and the ENGINEERING board of engineering.
	lanes: VocabLane[];
	// The product board: only engineering has one (empty for support).
	productLanes: VocabLane[];
	tiles: VocabTile[];
	// Statuses that mean the work is done, including grandfathered ones that are
	// not offered.
	finishedStatuses: string[];
	// Statuses the work has been handed back in: engineering has stopped and the
	// reporter may still have to act. Every finished status is one.
	handedBackStatuses: string[];
	// Old stored values a row may still hold. Never offered in a picker, filter or
	// bulk action; label, tone, lane and finished are as they were.
	retiredStatuses: VocabStatus[];
}

// ---------------------------------------------------------------------------
// Support: today's entries, unchanged.
// ---------------------------------------------------------------------------

// The six types offered, A to Z (Marcie R4): the labels are the TicketType labels.
const SUPPORT_TYPES: VocabOption[] = [
	{ value: 'bug', label: 'Bug' },
	{ value: 'chore', label: 'Change/Task Request' },
	{ value: 'enhancement', label: 'Enhancement' },
	{ value: 'feature', label: 'Feature' },
	{ value: 'help_wanted', label: 'Help Wanted' },
	{ value: 'question', label: 'Question' },
];

// The eight statuses offered on the list filter and bulk update (Marcie R5/R6).
// `open` is also the virtual "anything not resolved or closed" filter.
const SUPPORT_STATUSES: VocabStatus[] = [
	{ value: 'open', label: 'Open', tone: 'blue', finished: false },
	// Round 6 (contract section 12): two statuses of their own, staff only. The
	// first is not folded into Open; the second sits in With Engineering.
	{ value: 'pending_support_general', label: 'Pending Support General', tone: 'amber', finished: false },
	{ value: 'new', label: 'New', tone: 'blue', finished: false },
	{ value: 'triaging', label: 'Triaging', tone: 'blue', finished: false },
	{ value: 'waiting_on_partner', label: 'Waiting on Partner', tone: 'amber', finished: false },
	{ value: 'customer_replied', label: 'New Partner Reply', tone: 'amber', finished: false },
	{ value: 'waiting_on_dev', label: 'With Engineering', tone: 'amber', finished: false },
	{ value: 'update_for_dev_pending', label: 'UPDATE for Dev Pending', tone: 'amber', finished: false },
	{ value: 'resolved', label: 'Resolved', tone: 'green', finished: true },
	{ value: 'closed', label: 'Closed', tone: 'grey', finished: true },
];

// The board. Each lane lists every status it shows, including the statuses the
// Freshdesk import brought (Marcie ticket round 2, kanban 3a). A drop writes the
// lane's own dropStatus, a status COS keeps.
const SUPPORT_LANES: VocabLane[] = [
	{ key: 'open', label: 'Open', dropStatus: 'open', statuses: ['new', 'open', 'triaging', 'estimation', 'pending_support_general'] },
	{
		key: 'with_eng', label: 'With Engineering', dropStatus: 'waiting_on_dev',
		statuses: ['waiting_on_dev', 'in_progress', 'code_review', 'qa', 'release_queue', 'waiting_for_build', 'pending_dev_work', 'update_for_dev_pending'],
	},
	{
		key: 'waiting', label: 'Waiting on Partner', dropStatus: 'waiting_on_partner',
		statuses: ['waiting_on_partner', 'customer_replied', 'on_hold', 'pending'],
	},
	{ key: 'resolved', label: 'Resolved', dropStatus: 'resolved', statuses: ['resolved'] },
	{ key: 'closed', label: 'Closed', dropStatus: 'closed', statuses: ['closed'] },
];

// Statuses a support card on the board says by name, in a chip of their own, because
// the lane they sit in (Open, With Engineering) is broader than they are.
export const SUPPORT_STATUSES_WITH_OWN_CHIP: readonly string[] = ['pending_support_general', 'update_for_dev_pending'];

const SUPPORT_TILES: VocabTile[] = [
	{ key: 'total', label: 'Total', tone: 'neutral', filter: {} },
	{ key: 'active', label: 'Active', tone: 'neutral', filter: { status: 'open' } },
	{ key: 'overdue', label: 'Overdue', tone: 'danger', filter: { risk: 'overdue' } },
	{ key: 'waiting_customer', label: 'Waiting on Partner', tone: 'grey', filter: { status: 'waiting_on_partner' } },
	// Staff only: the summary answer carries `engineering_back` for staff and never for a
	// library login, and the summary shows this tile only when it does (contract 21 g).
	{ key: 'engineering_back', label: 'Back from Engineering', tone: 'warning', filter: { engineering_back: '1' } },
	{ key: 'unassigned', label: 'Unassigned', tone: 'warning', filter: { risk: 'unassigned' } },
	{ key: 'at_risk', label: 'At risk', tone: 'warning', filter: { risk: 'any' } },
];

// ---------------------------------------------------------------------------
// Engineering: the steps as the engineering leads gave them (contract section 21,
// 12 October 2026). THE ONLY place a step, type, lane or tile of an engineering
// ticket is named: change one here and in ticketVocabulary.test.ts, nowhere else.
// ---------------------------------------------------------------------------

// Bug, Feature, Enhancement, Chore, Investigation, Epic. Initiative, UAT and
// onboarding story stay valid on old rows but are not offered.
const ENGINEERING_TYPES: VocabOption[] = [
	{ value: 'bug', label: 'Bug' },
	{ value: 'feature', label: 'Feature' },
	{ value: 'enhancement', label: 'Enhancement' },
	{ value: 'chore', label: 'Chore' },
	{ value: 'investigation', label: 'Investigation' },
];
// Epic and initiative are not offered (28 October 2026): an engineering epic is a project
// (type engineering_epic). An old row keeps its type, and the screens still name it.

// The thirteen steps, in order: what every picker, filter and bulk action offers.
// Later on 21 October 2026: Needs Attention is a flag (tickets.needs_attention), not a
// step, and Blocked is Awaiting Business Action; both retired.
// 21 October 2026 (Kieran, engineering lead): Ready for Agent and Agent Finished
// are retired, since who holds a ticket is its hand-over, not its step; agent and
// engineer work walk the same steps. Engineer Pickup reads "Ready to build".
const ENGINEERING_STATUSES: VocabStatus[] = [
	{ value: 'open', label: 'To Do', tone: 'blue', finished: false },
	{ value: 'ready_for_shaping', label: 'Ready for Shaping', tone: 'blue', finished: false, agentWords: 'Under review' },
	{ value: 'engineer_pickup', label: 'Ready to build', tone: 'blue', finished: false, agentWords: 'Being built' },
	{ value: 'in_progress', label: 'In Progress', tone: 'amber', finished: false, agentWords: 'Being built' },
	{ value: 'code_review', label: 'In Peer Review', tone: 'amber', finished: false, agentWords: 'In review' },
	{ value: 'awaiting_business_action', label: 'Awaiting Business Action', tone: 'amber', finished: false, agentWords: 'Waiting on you' },
	{ value: 'ready_for_testing', label: 'Ready for Testing', tone: 'amber', finished: false, agentWords: 'Ready to test' },
	{ value: 'qa', label: 'In Test', tone: 'amber', finished: false },
	{ value: 'release_queue', label: 'Ready for Release', tone: 'amber', finished: false, agentWords: 'Ready for release' },
	{ value: 'awaiting_reporter_action', label: 'Awaiting Reporter Action', tone: 'blue', finished: false, agentWords: 'Released' },
	{ value: 'resolved', label: 'Done', tone: 'green', finished: true, agentWords: 'Done' },
	{ value: 'fixed_by_other', label: 'Fixed by Other', tone: 'grey', finished: true, agentWords: 'Done' },
	{ value: 'cancelled', label: 'Cancelled', tone: 'grey', finished: true, agentWords: 'Done' },
];

// Retired: valid on an old row, never offered, labelled as they always were. Each
// still sits in the lane it sat in (see the lanes below).
const ENGINEERING_RETIRED_STATUSES: VocabStatus[] = [
	{ value: 'new', label: 'New', tone: 'blue', finished: false },
	{ value: 'estimation', label: 'Estimation', tone: 'blue', finished: false },
	{ value: 'waiting_for_build', label: 'Waiting for Build', tone: 'amber', finished: false },
	{ value: 'closed', label: 'Closed', tone: 'grey', finished: true, agentWords: 'Done' },
	{ value: 'ready_for_agent', label: 'Ready for Agent', tone: 'blue', finished: false, agentWords: 'Being built' },
	{ value: 'agent_finished', label: 'Agent Finished', tone: 'blue', finished: false, agentWords: 'In review' },
	{ value: 'needs_attention', label: 'Needs Attention', tone: 'red', finished: false, agentWords: 'With engineering' },
	{ value: 'blocked', label: 'Blocked', tone: 'red', finished: false, agentWords: 'Waiting on you' },
];

// The engineering board: seven lanes from Ready to build to Ready for Release,
// nothing before and nothing after (Kieran: engineers do not want the backlog).
const ENGINEERING_LANES: VocabLane[] = [
	{ key: 'pickup', label: 'Ready to build', dropStatus: 'engineer_pickup', statuses: ['engineer_pickup', 'ready_for_agent'], backlogOrder: true },
	{ key: 'in_progress', label: 'In Progress', dropStatus: 'in_progress', statuses: ['in_progress'] },
	{ key: 'peer_review', label: 'In Peer Review', dropStatus: 'code_review', statuses: ['code_review', 'agent_finished'] },
	// Old Needs Attention and Blocked rows sit here; the flag shows on the card.
	{ key: 'business', label: 'Awaiting Business Action', dropStatus: 'awaiting_business_action', statuses: ['awaiting_business_action', 'blocked', 'needs_attention'] },
	{ key: 'ready_for_testing', label: 'Ready for Testing', dropStatus: 'ready_for_testing', statuses: ['ready_for_testing'] },
	{ key: 'test', label: 'In Test', dropStatus: 'qa', statuses: ['qa'] },
	{ key: 'release', label: 'Ready for Release', dropStatus: 'release_queue', statuses: ['release_queue', 'waiting_for_build'] },
];

// The product board: three lanes for the people who shape work before it is
// built; the last is the hand over.
const ENGINEERING_PRODUCT_LANES: VocabLane[] = [
	{ key: 'todo', label: 'To Do', dropStatus: 'open', statuses: ['open', 'new'], backlogOrder: true },
	{ key: 'shaping', label: 'Ready for Shaping', dropStatus: 'ready_for_shaping', statuses: ['ready_for_shaping', 'estimation'], backlogOrder: true },
	{ key: 'pickup', label: 'Ready to build', dropStatus: 'engineer_pickup', statuses: ['engineer_pickup', 'ready_for_agent'], backlogOrder: true },
];

// Each tile is a list filter, held in the address. `open=1` is "not finished"
// (Awaiting Reporter Action counts as open); `status` takes a comma-separated list.
// Who holds the ticket (20 October 2026): `driver` is a list of agent, author, engineer
// and none (no agent log). In Review or Test and Unassigned are engineering's own
// (driver engineer or none); With the reporter is `with_reporter=1`: not finished and
// (Awaiting Reporter Action or held by the reporter). Each tile equals the list it opens.
const ENGINEERING_TILES: VocabTile[] = [
	{ key: 'open', label: 'Open', tone: 'neutral', filter: { open: '1' } },
	{ key: 'in_progress', label: 'In Progress', tone: 'neutral', filter: { status: 'in_progress' } },
	{ key: 'in_review_or_test', label: 'In Review or Test', tone: 'neutral', filter: { status: 'code_review,ready_for_testing,qa', mode: 'engineer' } },
	{ key: 'needs_attention', label: 'Needs Attention', tone: 'danger', filter: { needs_attention: '1' } },
	{ key: 'awaiting_reporter', label: 'With the reporter', tone: 'warning', filter: { with_reporter: '1' } },
	{ key: 'unassigned', label: 'Unassigned', tone: 'warning', filter: { unassigned: '1', mode: 'engineer', exclude_status: 'ready_for_shaping' } },
];

const ENGINEERING_FINISHED = ['resolved', 'fixed_by_other', 'cancelled', 'closed'];

export const TICKET_VOCABULARY: Readonly<Record<TicketKind, KindVocabulary>> = {
	support: {
		kind: 'support',
		label: 'Support',
		types: SUPPORT_TYPES,
		statuses: SUPPORT_STATUSES,
		// Every status the plain label map holds, minus the ones only engineering carries.
		pageStatuses: Object.entries(TicketStatus)
			.filter(([value]) => !(value in ENGINEERING_ONLY_TICKET_STATUS_LABELS))
			.map(([value, label]) => ({ value, label })),
		lanes: SUPPORT_LANES,
		productLanes: [],
		tiles: SUPPORT_TILES,
		finishedStatuses: ['resolved', 'closed', 'done', 'archived'],
		handedBackStatuses: [],
		retiredStatuses: [],
	},
	engineering: {
		kind: 'engineering',
		label: 'Engineering',
		types: ENGINEERING_TYPES,
		statuses: ENGINEERING_STATUSES,
		pageStatuses: ENGINEERING_STATUSES.map((s) => ({ value: s.value, label: s.label })),
		lanes: ENGINEERING_LANES,
		productLanes: ENGINEERING_PRODUCT_LANES,
		tiles: ENGINEERING_TILES,
		finishedStatuses: ENGINEERING_FINISHED,
		handedBackStatuses: ['awaiting_reporter_action', ...ENGINEERING_FINISHED],
		retiredStatuses: ENGINEERING_RETIRED_STATUSES,
	},
};

// Anything that is not exactly 'engineering' is support, so an absent or
// unexpected kind on the wire never breaks a screen.
export function normalizeKind(kind: string | null | undefined): TicketKind {
	return kind === 'engineering' ? 'engineering' : 'support';
}

export function vocabFor(kind: string | null | undefined): KindVocabulary {
	return TICKET_VOCABULARY[normalizeKind(kind)];
}

export function typeOptions(kind: string | null | undefined): VocabOption[] {
	return vocabFor(kind).types.map((t) => ({ value: t.value, label: t.label }));
}

export function pageStatusOptions(kind: string | null | undefined): VocabOption[] {
	return vocabFor(kind).pageStatuses.map((s) => ({ value: s.value, label: s.label }));
}

export function statusOptions(kind: string | null | undefined): VocabOption[] {
	return vocabFor(kind).statuses.map((s) => ({ value: s.value, label: s.label }));
}

// The label for ANY stored status of the kind, offered or not. A status the
// kind does not list falls back to the shared label map, so a grandfathered row
// still reads properly.
/** The steps a reporter tests in (24 October 2026): Ready for Testing and In Test. */
export const ENGINEERING_TESTING_STEPS: readonly string[] = ['ready_for_testing', 'qa'];

/** Ready for Release (23 October 2026). A move here, or into testing, with subtasks not done or defects open is warned by the server (27 October 2026). */
export const ENGINEERING_RELEASE_STEP = 'release_queue';
/** The step a product review runs at (Start product review, 3 November 2026). */
export const ENGINEERING_REVIEW_STEP = 'ready_for_shaping';

/** Awaiting Reporter Action: released, and the reporter confirms the fix. */
export const ENGINEERING_REPORTER_STEP = 'awaiting_reporter_action';

// Sizing (23 October 2026): an engineering ticket's T-shirt size, in place of story
// points on screen. The stored values are the server's (model.TicketSizes).
export const TICKET_SIZES: ReadonlyArray<{ value: string; label: string }> = [
	{ value: 's', label: 'Small' },
	{ value: 'm', label: 'Medium' },
	{ value: 'l', label: 'Large' },
	{ value: 'xl', label: 'Extra Large' },
];

/** The words for a size; '' for none (or a value the table does not know). */
export function sizeLabel(size: string | null | undefined): string {
	return TICKET_SIZES.find((s) => s.value === size)?.label ?? '';
}

/** The Sizing field's choices: not sized, then the four sizes. */
export const SIZE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [{ value: '', label: 'Not sized' }, ...TICKET_SIZES];

export function statusLabel(kind: string | null | undefined, status: string | null | undefined): string {
	if (!status) return '';
	const hit = knownStatus(kind, status);
	if (hit) return hit.label;
	return displayLabel('TicketStatus', status);
}

export function statusTone(kind: string | null | undefined, status: string | null | undefined): StatusTone | undefined {
	return knownStatus(kind, status)?.tone;
}

// The requester's words for an engineering step an agent crew is driving the
// ticket through; a step with none reads as its label.
export function agentStatusWords(status: string | null | undefined): string {
	const hit = knownStatus('engineering', status);
	return hit?.agentWords ?? statusLabel('engineering', status);
}

// An offered status or a retired one of the kind: the pair a row can hold.
function knownStatus(kind: string | null | undefined, status: string | null | undefined): VocabStatus | undefined {
	const v = vocabFor(kind);
	return v.statuses.find((s) => s.value === status) ?? v.retiredStatuses.find((s) => s.value === status);
}

// The step a reopened ticket goes back to: the first the kind offers (support Open,
// engineering To Do).
export function reopenStatus(kind: string | null | undefined): string {
	return vocabFor(kind).statuses[0].value;
}

// The step the one-click Done action writes: the first finished step the kind offers.
export function doneStatus(kind: string | null | undefined): string {
	return (vocabFor(kind).statuses.find((s) => s.finished) ?? vocabFor(kind).statuses[0]).value;
}

export function isRetired(kind: string | null | undefined, status: string | null | undefined): boolean {
	return !!status && vocabFor(kind).retiredStatuses.some((s) => s.value === status);
}

// The engineering types the screens act on by name: the one a new ticket starts as, the
// one that grouped other tickets (an epic ticket: a support epic, or an old engineering
// row, still shows its tickets), and the one whose description gets the four bug
// headings. Named here so no screen writes the word.
const ENGINEERING_TYPE_ROLES = { start: 'feature', group: 'epic', defect: 'bug' } as const;
export function engineeringType(role: keyof typeof ENGINEERING_TYPE_ROLES): string {
	return ENGINEERING_TYPE_ROLES[role];
}

// The statuses a ticket's own page offers in its Status box. A ticket holding a
// retired value (or one the kind does not list) sees it first, as the current
// value, so the box shows the truth and never silently replaces it.
export function pageStatusOptionsFor(kind: string | null | undefined, current: string | null | undefined): VocabOption[] {
	const opts = pageStatusOptions(kind);
	if (!current || opts.some((o) => o.value === current)) return opts;
	return [{ value: current, label: statusLabel(kind, current) }, ...opts];
}

export function isFinished(kind: string | null | undefined, status: string | null | undefined): boolean {
	return !!status && vocabFor(kind).finishedStatuses.includes(status);
}

// The lanes of one board. Support has the one; engineering has `board` and `product`.
export function lanesOf(kind: string | null | undefined, board: BoardKey = 'board'): VocabLane[] {
	const v = vocabFor(kind);
	return board === 'product' ? v.productLanes : v.lanes;
}

export function laneFor(kind: string | null | undefined, status: string, board: BoardKey = 'board'): VocabLane | undefined {
	return lanesOf(kind, board).find((l) => l.statuses.includes(status));
}

// The stored statuses a summary tile of the kind counts, as the comma-separated list its filter sends
// ('' for a tile that filters on something else).
export function tileStatuses(kind: string | null | undefined, tileKey: string): string {
	return vocabFor(kind).tiles.find((t) => t.key === tileKey)?.filter.status ?? '';
}

// The whole filter a summary tile of the kind sends (a copy; {} for an unknown tile), so a screen
// that lists what a tile counts asks for exactly the tile's list.
export function tileFilter(kind: string | null | undefined, tileKey: string): Record<string, string> {
	return { ...(vocabFor(kind).tiles.find((t) => t.key === tileKey)?.filter ?? {}) };
}

export function isHandedBack(kind: string | null | undefined, status: string | null | undefined): boolean {
	return !!status && vocabFor(kind).handedBackStatuses.includes(status);
}

// The lane a ticket sits in on a BOARD. A ticket on neither board (handed back, so
// Awaiting Reporter Action, Done, Fixed by Other, Cancelled, Closed) has no lane.
// An unfinished engineering ticket whose status no lane of either board names (an
// old imported value) goes in the FIRST lane of the PRODUCT board and never on the
// engineering board. Support keeps its own lanes as they are.
export function boardLane(kind: string | null | undefined, status: string, board: BoardKey = 'board'): VocabLane | undefined {
	const hit = laneFor(kind, status, board);
	if (hit) return hit;
	if (normalizeKind(kind) !== 'engineering' || board !== 'product') return undefined;
	if (isFinished(kind, status) || isHandedBack(kind, status)) return undefined;
	if (laneFor(kind, status, 'board')) return undefined;
	return vocabFor(kind).productLanes[0];
}

// The type or status values the kind offers, for resetting a filter that does not
// exist in the other kind.
export function offersStatus(kind: string | null | undefined, status: string): boolean {
	return vocabFor(kind).statuses.some((s) => s.value === status);
}

export function offersType(kind: string | null | undefined, type: string): boolean {
	return vocabFor(kind).types.some((t) => t.value === type);
}

// What one board asks the server for, a lane at a time, so every row it receives is a card it can draw
// and a lane with more than a page says so. A lane asks for the statuses it names (narrowed to the ones
// a person chose, when one did). The FIRST lane of the product board also takes an unfinished ticket in
// a status no lane of either board names, so it cannot list its statuses: it asks for open work except
// the statuses every other lane, of either board, and the handed back steps name.
export interface LaneAsk {
	lane: string;
	// The statuses to ask for (comma list), or '' to ask for open work except `exclude`.
	status: string;
	exclude: string;
}

export function laneAsks(kind: string | null | undefined, board: BoardKey, chosen: string[] | null): LaneAsk[] {
	const v = vocabFor(kind);
	const lanes = lanesOf(kind, board);
	const named = (ls: VocabLane[]) => ls.flatMap((l) => l.statuses);
	const asks: LaneAsk[] = [];
	lanes.forEach((l, i) => {
		if (board === 'product' && i === 0 && normalizeKind(kind) === 'engineering') {
			const others = new Set([
				...named(lanes.slice(1)), ...named(v.lanes),
				...v.handedBackStatuses.filter((s) => !v.finishedStatuses.includes(s)),
			]);
			for (const s of l.statuses) others.delete(s);
			if (chosen && !chosen.some((s) => l.statuses.includes(s) || !others.has(s))) return;
			asks.push({ lane: l.key, status: chosen ? chosen.join(',') : '', exclude: [...others].join(',') });
			return;
		}
		const statuses = chosen ? l.statuses.filter((s) => chosen.includes(s)) : l.statuses;
		if (statuses.length > 0) asks.push({ lane: l.key, status: statuses.join(','), exclude: '' });
	});
	return asks;
}
